import React, { useState } from 'react';
import { X, Download, ZoomIn, ZoomOut } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';


const AttachmentViewer = ({ emailId, attachment, attachmentIndex, onClose }) => {
    const [zoom, setZoom] = useState(100);

    const getAttachmentUrl = () => {
        return `${API}/mailbox/attachment/${emailId}/${attachmentIndex}`;
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = getAttachmentUrl();
        link.download = attachment.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderViewer = () => {
        const contentType = attachment.content_type.toLowerCase();
        const url = getAttachmentUrl();

        // PDF Viewer
        if (contentType.includes('pdf')) {
            return (
                <iframe
                    src={url}
                    className="w-full h-full"
                    title={attachment.filename}
                />
            );
        }

        // Image Viewer
        if (contentType.includes('image')) {
            return (
                <div className="flex items-center justify-center h-full bg-slate-100 dark:bg-slate-900 overflow-auto">
                    <img
                        src={url}
                        alt={attachment.filename}
                        style={{ transform: `scale(${zoom / 100})` }}
                        className="max-w-full max-h-full object-contain transition-transform"
                    />
                </div>
            );
        }

        // Text Viewer
        if (contentType.includes('text') || contentType.includes('json') || contentType.includes('xml')) {
            return (
                <iframe
                    src={url}
                    className="w-full h-full bg-white dark:bg-slate-900"
                    title={attachment.filename}
                />
            );
        }

        // Office Documents (if browser supports)
        if (contentType.includes('word') || contentType.includes('excel') || contentType.includes('powerpoint') ||
            contentType.includes('document') || contentType.includes('spreadsheet') || contentType.includes('presentation')) {
            return (
                <div className="flex flex-col items-center justify-center h-full p-8">
                    <div className="text-center mb-6">
                        <p className="text-slate-600 dark:text-slate-400 mb-2">
                            Preview not available for this file type
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-500">
                            {attachment.filename}
                        </p>
                    </div>
                    <button
                        onClick={handleDownload}
                        className="px-6 py-3 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg hover:shadow-lg transition-all flex items-center space-x-2"
                    >
                        <Download className="w-5 h-5" />
                        <span>Download to View</span>
                    </button>
                </div>
            );
        }

        // Default - unsupported type
        return (
            <div className="flex flex-col items-center justify-center h-full p-8">
                <div className="text-center mb-6">
                    <p className="text-slate-600 dark:text-slate-400 mb-2">
                        Preview not available for this file type
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-500">
                        {attachment.filename}
                    </p>
                </div>
                <button
                    onClick={handleDownload}
                    className="px-6 py-3 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg hover:shadow-lg transition-all flex items-center space-x-2"
                >
                    <Download className="w-5 h-5" />
                    <span>Download File</span>
                </button>
            </div>
        );
    };

    const isImage = attachment.content_type.toLowerCase().includes('image');

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/80 z-50 flex flex-col"
            >
                {/* Header */}
                <div className="bg-slate-900 text-white p-4 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold truncate">{attachment.filename}</h3>
                        <p className="text-sm text-slate-400">
                            {(attachment.size / 1024).toFixed(1)} KB • {attachment.content_type}
                        </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                        {isImage && (
                            <>
                                <button
                                    onClick={() => setZoom(Math.max(25, zoom - 25))}
                                    className="p-2 hover:bg-slate-800 rounded transition-colors"
                                    title="Zoom Out"
                                >
                                    <ZoomOut className="w-5 h-5" />
                                </button>
                                <span className="text-sm text-slate-400 min-w-[60px] text-center">
                                    {zoom}%
                                </span>
                                <button
                                    onClick={() => setZoom(Math.min(200, zoom + 25))}
                                    className="p-2 hover:bg-slate-800 rounded transition-colors"
                                    title="Zoom In"
                                >
                                    <ZoomIn className="w-5 h-5" />
                                </button>
                                <div className="w-px h-6 bg-slate-700 mx-2"></div>
                            </>
                        )}
                        <button
                            onClick={handleDownload}
                            className="p-2 hover:bg-slate-800 rounded transition-colors"
                            title="Download"
                        >
                            <Download className="w-5 h-5" />
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-slate-800 rounded transition-colors"
                            title="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Viewer Content */}
                <div className="flex-1 overflow-hidden">
                    {renderViewer()}
                </div>
            </motion.div>
        </AnimatePresence>
    );
};

export default AttachmentViewer;
